import { BasePagination, Text } from '@elkaso-app/web-design';
import { IInvoiceScanData, useGetInvoiceScansApi } from 'apis/invoice-scanning';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CellProps } from 'react-table';
import { useHistory } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { InvoiceScanRestaurantFilterButton } from '../components/filters/restaurant-filter-button';
import { InvoiceScanStatusFilterButton } from '../components/filters/status-filter-button';
import { DeleteScanModal } from '../components/modals/delete-scan-modal';
import { ActionsCell, IActionsCell } from '../components/table-cells/actions-cell';
import { AmountCell, IAmountCell } from '../components/table-cells/amount-cell';
import { IProcessingStatusBadge, ProcessingStatusBadge } from '../components/table-cells/processing-status-badge';
import { IValidationStatusBadge, ValidationStatusBadge } from '../components/table-cells/validation-status-badge';

export const InvoiceScansListScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const setPaginationParams = useSetPaginationParamsV1();

  const [selectedScan, setSelectedScan] = useState<IInvoiceScanData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data, isFetching, isLoading } = useGetInvoiceScansApi();

  useEffect(() => {
    dispatch(changeTitle('Invoice Scanning'));
  }, [dispatch]);

  const handleView = useCallback(
    (scanId: number) => {
      history.push(`/app/invoice-scanning/view/${scanId}`);
    },
    [history]
  );

  const handleDelete = useCallback(
    (scanId: number) => {
      const scan = data?.data?.find((s) => s.id === scanId);
      if (scan) {
        setSelectedScan(scan);
        setIsDeleteModalOpen(true);
      }
    },
    [data?.data]
  );

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedScan(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Invoice #' />,
        accessor: 'invoiceNumber',
        Cell: ({ value }: CellProps<any, string | null>) => <Text>{value || '---'}</Text>,
      },
      {
        Header: <TableHeader name='Vendor' />,
        accessor: 'vendorName',
        Cell: ({ value }: CellProps<any, string | null>) => <Text>{value || '---'}</Text>,
      },
      {
        Header: <TableHeader name='Order ID' />,
        accessor: 'orderId',
        Cell: ({ value }: CellProps<any, number>) => <Text>{value}</Text>,
      },
      {
        Header: <TableHeader name='Restaurant' filterButtonComponent={<InvoiceScanRestaurantFilterButton />} />,
        accessor: 'restaurantId',
        Cell: ({ row }: CellProps<any, any>) => <Text>{row.original.order?.restaurantId || '---'}</Text>,
      },
      {
        Header: <TableHeader name='Total Amount' />,
        accessor: 'totalAmount',
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IAmountCell>
            as={AmountCell}
            amount={row.original.totalAmount}
            currency={row.original.currency}
          />
        ),
      },
      {
        Header: <TableHeader name='Status' filterButtonComponent={<InvoiceScanStatusFilterButton />} />,
        accessor: 'status',
        Cell: ({ value }: CellProps<any, string>) => (
          <CustomTableCell<IProcessingStatusBadge> as={ProcessingStatusBadge} status={value} />
        ),
      },
      {
        Header: <TableHeader name='Validation' />,
        accessor: 'validationStatus',
        Cell: ({ value }: CellProps<any, string>) => (
          <CustomTableCell<IValidationStatusBadge> as={ValidationStatusBadge} status={value} />
        ),
      },
      {
        Header: <TableHeader name='Items' />,
        accessor: 'itemCount',
        Cell: ({ value }: CellProps<any, string>) => <Text>{value || '0'}</Text>,
      },
      {
        Header: <TableHeader name='Uploaded' />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, string>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Uploader' />,
        accessor: 'uploadedByUser',
        Cell: ({ value }: CellProps<any, { id: number; name: string }>) => <Text>{value?.name || '---'}</Text>,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'id',
        Cell: ({ value }: CellProps<any, number>) => (
          <CustomTableCell<IActionsCell> as={ActionsCell} scanId={value} onView={handleView} onDelete={handleDelete} />
        ),
      },
    ],
    [handleView, handleDelete]
  );

  const tableData = useMemo(() => data?.data || [], [data?.data]);

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Invoice Scans' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>
        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={data?.pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={data?.total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>

      {selectedScan && (
        <DeleteScanModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteModalClose}
          scanId={selectedScan.id}
          invoiceNumber={selectedScan.invoiceNumber}
          vendorName={selectedScan.vendorName}
        />
      )}
    </BaseTableContainer>
  );
};
